<script setup lang="ts">
import type { ResolvedMonospaceSingleRelationField } from '#layers/data-model/types/schema/resolved-schema';
import { computed, toRefs } from 'vue';
import { provideBlockInjection } from '#layers/block-engine';
import { useInputBlockDisabled } from '~/composables/form-block/use-base-form-field-state';
import { useRelationFieldState } from '~/composables/form-block/use-relation-field-state';
import { provideRelationFormFieldContext } from '~/composables/form-block/use-relation-form-field';
import { useDisplayNames } from '~/composables/use-display-names';
import { getSingleRelationFieldInputBlockOptions } from '~/utils/fields/single-relation-field';

const props = defineProps<{
	relationField: ResolvedMonospaceSingleRelationField;
	label?: string;
	description?: string;
	name?: string;
	required?: boolean;
	ejected?: boolean;
}>();

const { relationField } = toRefs(props);

const { getSingleRelationFieldDisplayName } = useDisplayNames();
const label = computed(() => props.label ?? (relationField.value ? getSingleRelationFieldDisplayName(relationField.value) : ''));
const description = computed(() => props.description ?? relationField.value?.meta?.description ?? undefined);
const name = computed(() => props.name ?? relationField.value?.apiName);

const { referencedItemKey, handleCreate, handleUndoCreate, handleConnect, handleUndoConnect, handleDisconnect, handleUndoDisconnect, handleDelete, handleUndoDelete, handleUpdate, fieldValue } = useRelationFieldState(() => props.relationField);
const inputBlockProps = computed(() => ({
	...getSingleRelationFieldInputBlockOptions(relationField.value),
	fieldId: relationField.value.id,
	referencedItemKey: referencedItemKey.value,
}));

const blockDisabled = useInputBlockDisabled(inputBlockProps);

provideRelationFormFieldContext({
	field: relationField,
	referencedItemKey,
	fieldValue,
	disabled: blockDisabled,
	emitCreate: handleCreate,
	emitUndoCreate: handleUndoCreate,
	emitConnect: handleConnect,
	emitUndoConnect: handleUndoConnect,
	emitDisconnect: handleDisconnect,
	emitUndoDisconnect: handleUndoDisconnect,
	emitDelete: handleDelete,
	emitUndoDelete: handleUndoDelete,
	emitUpdate: handleUpdate,
});

provideBlockInjection({
	fieldId: () => relationField.value.id,
	referencedItemKey,
});
</script>

<template>
	<DFormField
		:name
		:label
		:description
		:required
	>
		<template v-if="!ejected && inputBlockProps">
			<BlockRender v-bind="inputBlockProps" />
		</template>
		<slot v-else />
	</DFormField>
</template>
